// Device system model (Domain 07): flash file system, IOS images, boot sequence,
// configuration register, configuration files and a TFTP server on a Windows host.
//
//   d.sys = {
//     flash:   { size, files: [ File ] }            directory order matters (boot fallback)
//     boot:    [ 'flash:<file>', ... ]               boot system statements (running-config)
//     confreg: '0x2102'   confregNext: null|'0x2142'  takes effect at the next reload
//     image:   { file, version, platform }            what is running now
//     rommon:  false | { n }                           device sits in ROM monitor
//     uptime:  ms timestamp of the last boot (net.clock based)   reloads: n
//     factory: JSON string of the device with no configuration (hardware only)
//     cdp: true, lldp: false                           global discovery protocols
//   }
//   File = { name, size, date, kind: 'image'|'config'|'text', md5, image?: { platform, version }, lines?: [] }
//   host.tftp = { enabled, root: 'C:\\TFTP-Root', files: { [name]: File }, log: [] }
//
// Modelling simplifications (stated in the Domain 07 missions):
//   * an IOS upgrade is simulated: images are file records with a size, a version and
//     a checksum — nothing is installed, and no real device is contacted;
//   * MD5 values are lab values from a simple digest, in the real 32-hex format;
//   * a reload rebuilds the device from its hardware ("factory") state and replays the
//     saved startup-config text through the real CLI parser, so only what was saved
//     survives — exactly the behaviour being taught; it completes instantly.

import { ipToInt } from './addr.js';

export const MB = 1024 * 1024;
/** Lab image catalog — fictional checksums, real naming scheme. */
export const IMAGES = {
  'c2900-universalk9-mz.SPA.155-3.M6.bin': { platform: 'c2900', version: '15.5(3)M6', size: 97794040 },
  'c2900-universalk9-mz.SPA.157-3.M8.bin': { platform: 'c2900', version: '15.7(3)M8', size: 107247520 },
  'c2900-universalk9-mz.SPA.159-3.M9.bin': { platform: 'c2900', version: '15.9(3)M9', size: 111986064 },
  'c1900-universalk9-mz.SPA.159-3.M9.bin': { platform: 'c1900', version: '15.9(3)M9', size: 86493112 },
  'c2960-lanbasek9-mz.150-2.SE4.bin': { platform: 'c2960', version: '15.0(2)SE4', size: 11832064 },
};

/** Deterministic 32-hex lab digest (not cryptographic). */
export function md5Of(text) {
  let h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    for (let k = 0; k < 4; k++) h[k] = Math.imul(h[k] ^ (c + k * 131), 0x01000193 + k * 2) >>> 0;
    h = [h[1], h[2], h[3], (h[0] + (h[3] << 7 | h[3] >>> 25)) >>> 0];
  }
  return h.map((x) => x.toString(16).padStart(8, '0')).join('');
}
export const imageMd5 = (name) => md5Of(`image:${name}`);
export function imageFile(name, { date = 'Mar 12 2025 10:14:22 +08:00', corrupt = false } = {}) {
  const i = IMAGES[name];
  return {
    name, kind: 'image', date, image: { platform: i.platform, version: i.version },
    size: corrupt ? Math.floor(i.size * 0.61) : i.size, md5: corrupt ? md5Of(`corrupt:${name}`) : imageMd5(name), ...(corrupt ? { corrupt: true } : {}),
  };
}
export function configFile(name, lines, date = 'Oct 14 2026 07:55:10 +08:00') {
  const text = lines.join('\n');
  return { name, kind: 'config', date, size: text.length + lines.length, md5: md5Of(text), lines: [...lines] };
}
export const platformOf = (d) => (d.kind === 'switch' ? 'c2960' : 'c2900');
export const isFlashPath = (t) => /^flash0?:/i.test(t);
export const flashName = (t) => t.replace(/^flash0?:\/?/i, '');

export function sysOf(d) { return d.sys || null; }
export const flashFile = (d, name) => d.sys?.flash.files.find((f) => f.name === name) || null;
export const flashUsed = (d) => d.sys.flash.files.reduce((a, f) => a + f.size, 0);
export const flashFree = (d) => d.sys.flash.size - flashUsed(d);
/** Is this file an image this device can boot? → null when fine, else the reason. */
export function bootProblem(d, f) {
  if (!f) return 'not-found';
  if (f.kind !== 'image') return 'not-image';
  if (f.corrupt || f.md5 !== imageMd5(f.name)) return 'corrupt';
  if (f.image.platform !== platformOf(d)) return 'platform';
  return null;
}

/**
 * Give a router / switch a system: flash with the running image, factory state, boot
 * defaults. `factory` is the same device built with no configuration.
 */
export function attachSystem(d, factory, { files, image, boot = [], flashSize = d.kind === 'switch' ? 64 * MB : 256 * MB } = {}) {
  const run = image || files.find((f) => f.kind === 'image')?.name;
  d.sys = {
    flash: { size: flashSize, files: files.map((f) => ({ ...f })) },
    boot: [...boot], confreg: d.kind === 'switch' ? null : '0x2102', confregNext: null,
    image: { file: run, version: IMAGES[run].version, platform: IMAGES[run].platform },
    rommon: false, reloads: 0, bootedAt: null,
    factory: JSON.stringify(factory), cdp: true, lldp: false,
  };
  return d;
}

/**
 * Boot sequence: the first valid image named by "boot system", else the first valid
 * image in flash; none → ROM monitor. Returns { image, messages, rommon }.
 */
export function chooseImage(d) {
  const s = d.sys;
  const msgs = [];
  const reg = (s.confregNext || s.confreg || '0x2102').toLowerCase();
  if ((parseInt(reg, 16) & 0xf) === 0) return { rommon: true, messages: ['', `Configuration register = ${reg}: boot to ROM monitor`, ''] };
  for (const b of s.boot) {
    const f = flashFile(d, flashName(b));
    const p = bootProblem(d, f);
    if (!p) return { image: f, messages: msgs };
    msgs.push(p === 'not-found' ? `%Error opening ${b} (File not found)` : p === 'platform' ? `%Error: ${b} is not a valid image for this platform` : `%Error: ${b} — image checksum mismatch, boot aborted`);
  }
  if (s.boot.length) msgs.push('Boot system statements failed. Trying the first image in flash...');
  for (const f of s.flash.files) if (f.kind === 'image' && !bootProblem(d, f)) return { image: f, messages: msgs };
  msgs.push('', '*** No valid IOS image found in flash ***');
  return { rommon: true, messages: msgs };
}
/** Startup is ignored when bit 6 (0x0040) of the configuration register is set. */
export const ignoresStartup = (reg) => !!reg && (parseInt(reg, 16) & 0x40) !== 0;

// ------------------------------------------------------------ TFTP server (Windows host)
export function tftpHost(net, ip) {
  return Object.values(net.devices).find((x) => x.tftp && (x.ipcfg?.ip === ip || x.lease?.ip === ip)) || null;
}
export const tftpPath = (root, name) => `${root}\\${name}`;
export const ipNum = (ip) => ipToInt(ip);

// ------------------------------------------------------------ CDP / LLDP view
export const cdpOn = (d, ifc) => d.sys ? d.sys.cdp !== false && d.interfaces[ifc]?.cdp !== false : true;
export const lldpOn = (d, ifc, dir = 'tx') => !!d.sys?.lldp && d.interfaces[ifc]?.[dir === 'tx' ? 'lldpTx' : 'lldpRx'] !== false;
